import { useState, useEffect, useRef, useMemo } from "react";
import { Link } from "@/lib/router";
import { useQuery } from "@tanstack/react-query";
import { agentsApi } from "../api/agents";
import { usePractice } from "../context/PracticeContext";
import { useBreadcrumbs } from "../context/BreadcrumbContext";
import { queryKeys } from "../lib/queryKeys";
import { AgentIcon } from "../components/AgentIconPicker";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { cn } from "../lib/utils";
import { agentStatusDot, agentStatusDotDefault } from "../lib/status-colors";
import { AGENT_TYPE_LABELS } from "@ligament/shared";
import type { Agent } from "@ligament/shared";
import { Bot, Send, ArrowRight, MessageSquare } from "lucide-react";
import { EmptyState } from "../components/EmptyState";

// ─── Types ──────────────────────────────────────────────────────────────────

interface Message {
  role: "user" | "assistant";
  content: string;
}

// ─── Agent list item ──────────────────────────────────────────────────────────

function AgentListItem({
  agent,
  selected,
  onSelect,
}: {
  agent: Agent;
  selected: boolean;
  onSelect: () => void;
}) {
  const meta = agent.metadata as Record<string, unknown> | null | undefined;
  const isPlatform = meta?.isPlatformAgent === true;
  const statusColor = agentStatusDot[agent.status] ?? agentStatusDotDefault;

  return (
    <button
      onClick={onSelect}
      className={cn(
        "flex items-center gap-2.5 w-full px-3 py-2 text-left transition-colors text-sm",
        selected
          ? "bg-accent text-foreground"
          : "hover:bg-accent/50 text-foreground/80 hover:text-foreground",
      )}
    >
      <div
        className={cn(
          "flex h-6 w-6 shrink-0 items-center justify-center rounded-md",
          isPlatform
            ? "bg-indigo-100 dark:bg-indigo-900/40"
            : "bg-muted",
        )}
      >
        <AgentIcon
          icon={agent.icon}
          className={cn(
            "h-3.5 w-3.5",
            isPlatform
              ? "text-indigo-600 dark:text-indigo-400"
              : "text-muted-foreground",
          )}
        />
      </div>
      <span className="flex-1 truncate text-xs font-medium">{agent.name}</span>
      <span className={cn("relative flex h-2 w-2 shrink-0")}>
        <span className={cn("absolute inline-flex h-full w-full rounded-full", statusColor)} />
      </span>
    </button>
  );
}

// ─── Chat bubble ─────────────────────────────────────────────────────────────

function ChatBubble({
  message,
  agentIcon,
}: {
  message: Message;
  agentIcon: string | null | undefined;
}) {
  const isUser = message.role === "user";
  return (
    <div className={cn("flex gap-2.5 items-end", isUser && "flex-row-reverse")}>
      {!isUser && (
        <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-muted">
          <AgentIcon icon={agentIcon} className="h-3.5 w-3.5 text-muted-foreground" />
        </div>
      )}
      <div
        className={cn(
          "max-w-[80%] rounded-lg px-3 py-2 text-sm whitespace-pre-wrap break-words",
          isUser
            ? "bg-primary text-primary-foreground rounded-br-none"
            : "bg-muted text-foreground rounded-bl-none",
        )}
      >
        {message.content}
      </div>
    </div>
  );
}

// ─── Loading bubble ───────────────────────────────────────────────────────────

function LoadingBubble({ agentIcon }: { agentIcon: string | null | undefined }) {
  return (
    <div className="flex gap-2.5 items-end">
      <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-muted">
        <AgentIcon icon={agentIcon} className="h-3.5 w-3.5 text-muted-foreground" />
      </div>
      <div className="bg-muted rounded-lg rounded-bl-none px-4 py-3">
        <span className="flex gap-1 items-center">
          <span className="h-1.5 w-1.5 rounded-full bg-muted-foreground/60 animate-bounce [animation-delay:0ms]" />
          <span className="h-1.5 w-1.5 rounded-full bg-muted-foreground/60 animate-bounce [animation-delay:150ms]" />
          <span className="h-1.5 w-1.5 rounded-full bg-muted-foreground/60 animate-bounce [animation-delay:300ms]" />
        </span>
      </div>
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export function AgentChat() {
  const { selectedPracticeId } = usePractice();
  const { setBreadcrumbs } = useBreadcrumbs();

  const [selectedAgentId, setSelectedAgentId] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [threads, setThreads] = useState<Record<string, Message[]>>({});
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setBreadcrumbs([{ label: "Agent Chat" }]);
  }, [setBreadcrumbs]);

  const { data: agents } = useQuery({
    queryKey: queryKeys.agents.list(selectedPracticeId!),
    queryFn: () => agentsApi.list(selectedPracticeId!),
    enabled: !!selectedPracticeId,
  });

  // Filter out sample agents, split into platform and regular
  const { platformAgents, regularAgents } = useMemo(() => {
    const visible = (agents ?? []).filter((a) => {
      const meta = a.metadata as Record<string, unknown> | null | undefined;
      return meta?.isSample !== true;
    });
    const platform = visible.filter((a) => {
      const meta = a.metadata as Record<string, unknown> | null | undefined;
      return meta?.isPlatformAgent === true;
    });
    const regular = visible.filter((a) => {
      const meta = a.metadata as Record<string, unknown> | null | undefined;
      return meta?.isPlatformAgent !== true;
    });
    return { platformAgents: platform, regularAgents: regular };
  }, [agents]);

  const searchLower = search.toLowerCase();
  const filteredPlatform = platformAgents.filter((a) =>
    a.name.toLowerCase().includes(searchLower),
  );
  const filteredRegular = regularAgents.filter((a) =>
    a.name.toLowerCase().includes(searchLower),
  );

  const selectedAgent = useMemo(
    () => (agents ?? []).find((a) => a.id === selectedAgentId) ?? null,
    [agents, selectedAgentId],
  );

  const currentThread = selectedAgentId ? (threads[selectedAgentId] ?? []) : [];

  // Scroll to bottom when messages change or loading state changes
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [currentThread, isLoading]);

  async function handleSend() {
    if (!input.trim() || !selectedAgentId || !selectedPracticeId || isLoading) return;

    const userMessage: Message = { role: "user", content: input.trim() };
    const updatedThread = [...currentThread, userMessage];

    setThreads((prev) => ({ ...prev, [selectedAgentId]: updatedThread }));
    setInput("");
    setIsLoading(true);
    setError(null);

    try {
      const response = await agentsApi.chat(
        selectedPracticeId,
        selectedAgentId,
        updatedThread,
      );
      setThreads((prev) => ({
        ...prev,
        [selectedAgentId]: [
          ...(prev[selectedAgentId] ?? []),
          { role: "assistant" as const, content: response.content },
        ],
      }));
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : "Failed to get a response. Please try again.";
      setError(message);
    } finally {
      setIsLoading(false);
    }
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }

  const agentTypeLabels = AGENT_TYPE_LABELS as Record<string, string>;

  if (!selectedPracticeId) {
    return <EmptyState icon={Bot} message="Select a practice to use Agent Chat." />;
  }

  return (
    <div className="flex h-[calc(100vh-8rem)] -mx-4 sm:-mx-6 border-t border-border">
      {/* Left panel — agent picker */}
      <div className="flex flex-col w-[280px] shrink-0 border-r border-border">
        {/* Search */}
        <div className="p-2 border-b border-border shrink-0">
          <Input
            placeholder="Search agents..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-8 text-xs"
          />
        </div>

        {/* Agent list */}
        <div className="flex-1 overflow-y-auto">
          {/* Platform agents section */}
          {filteredPlatform.length > 0 && (
            <div>
              <p className="px-3 pt-3 pb-1 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
                Platform
              </p>
              {filteredPlatform.map((agent) => (
                <AgentListItem
                  key={agent.id}
                  agent={agent}
                  selected={selectedAgentId === agent.id}
                  onSelect={() => {
                    setSelectedAgentId(agent.id);
                    setError(null);
                  }}
                />
              ))}
            </div>
          )}

          {/* Regular agents section */}
          {filteredRegular.length > 0 && (
            <div>
              {filteredPlatform.length > 0 && (
                <p className="px-3 pt-3 pb-1 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
                  Agents
                </p>
              )}
              {filteredRegular.map((agent) => (
                <AgentListItem
                  key={agent.id}
                  agent={agent}
                  selected={selectedAgentId === agent.id}
                  onSelect={() => {
                    setSelectedAgentId(agent.id);
                    setError(null);
                  }}
                />
              ))}
            </div>
          )}

          {filteredPlatform.length === 0 && filteredRegular.length === 0 && (
            <p className="px-3 py-4 text-xs text-muted-foreground text-center">
              {search ? "No agents match your search." : "No agents found."}
            </p>
          )}
        </div>
      </div>

      {/* Right panel — chat */}
      <div className="flex flex-col flex-1 min-w-0">
        {!selectedAgent ? (
          <div className="flex flex-1 items-center justify-center text-center p-8 text-muted-foreground">
            <div className="flex flex-col items-center gap-3">
              <div className="flex h-14 w-14 items-center justify-center rounded-full bg-muted">
                <MessageSquare className="h-6 w-6 text-muted-foreground/60" />
              </div>
              <p className="text-sm font-medium">Select an agent to start chatting</p>
              <p className="text-xs text-muted-foreground/70 max-w-xs">
                Choose an agent from the list on the left to begin a conversation.
              </p>
            </div>
          </div>
        ) : (
          <>
            {/* Chat header */}
            <div className="flex items-center gap-3 px-4 py-3 border-b border-border shrink-0">
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-muted">
                <AgentIcon icon={selectedAgent.icon} className="h-4 w-4 text-muted-foreground" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <h2 className="text-sm font-semibold truncate">{selectedAgent.name}</h2>
                  <Badge variant="secondary" className="text-[10px] px-1.5 py-0 h-4 shrink-0">
                    {agentTypeLabels[selectedAgent.agentType] ?? selectedAgent.agentType}
                  </Badge>
                </div>
              </div>
              <Link
                to={`/agents/${selectedAgent.id}`}
                className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors no-underline shrink-0"
              >
                View detail
                <ArrowRight className="h-3 w-3" />
              </Link>
            </div>

            {/* Message thread */}
            <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
              {currentThread.length === 0 && !isLoading && (
                <div className="flex items-center justify-center h-full text-center">
                  <div className="text-muted-foreground">
                    <p className="text-sm">Start the conversation</p>
                    <p className="text-xs mt-1 text-muted-foreground/70">
                      Send a message to{" "}
                      <span className="font-medium text-foreground/80">{selectedAgent.name}</span>
                    </p>
                  </div>
                </div>
              )}

              {currentThread.map((msg, idx) => (
                <ChatBubble
                  key={idx}
                  message={msg}
                  agentIcon={selectedAgent.icon}
                />
              ))}

              {isLoading && <LoadingBubble agentIcon={selectedAgent.icon} />}

              {error && (
                <div className="flex justify-center">
                  <div className="bg-destructive/10 text-destructive text-xs px-3 py-2 rounded-md max-w-sm text-center">
                    {error}
                  </div>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>

            {/* Input bar */}
            <div className="px-4 py-3 border-t border-border shrink-0">
              <div className="flex items-end gap-2">
                <textarea
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder={`Message ${selectedAgent.name}...`}
                  rows={2}
                  disabled={isLoading}
                  className={cn(
                    "flex-1 resize-none rounded-md border border-border bg-background px-3 py-2 text-sm",
                    "placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring",
                    "disabled:opacity-50 disabled:cursor-not-allowed",
                  )}
                />
                <Button
                  size="sm"
                  onClick={handleSend}
                  disabled={!input.trim() || isLoading}
                >
                  <Send className="h-3.5 w-3.5" />
                </Button>
              </div>
              <p className="mt-1 text-[10px] text-muted-foreground">
                Press Enter to send · Shift+Enter for new line
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
